import React, { FC } from "react";
import EfficiencyChart from "./EfficiencyChart";
import Sticker from "../../../../../components/elements/sticker/Sticker";
import st from './styles.module.scss';
import Idles from "./IdlesChart";
import PeriodSwitcher from "./components/PeriodSwitcher/PeriodSwitcher";
import { useTypedSelector } from "@hooks/useTypedSelector";
import { IdlesSliceState } from "../../../../../store/reducers/dashboard/works/idles";
import { EfficiencySliceState } from "../../../../../store/reducers/dashboard/works/efficiency";
import EmptyPage from "./empty";
import { ApiSelect } from "@baseTypes/baseType";
import { createSelector } from "@reduxjs/toolkit";
import { identity } from "fp-ts";
import { RootState } from "../../../../../store";
import { StickerErrorBox, stickerLoadingWithCrop } from "../../../phases";
import { getWorkDetailsRequestParams } from "../utils/getWorkDetailsRequestParams";
import LoadingBox from "../../../../../components/elements/loading/LoadingBox";
import { WorkDisplayPeriod } from "@baseTypes/service/dashboard/IWorksDashboardService";
import { omit } from "lodash";

type EfficiencyProps = {
    period: WorkDisplayPeriod
    setPeriod: (period: WorkDisplayPeriod) => void
}

const Efficiency: FC<EfficiencyProps> = ({ period, setPeriod }) => {
    const {
        isApiLoading: isIdlesLoading,
        apiError: idlesApiError
    } = useTypedSelector<IdlesSliceState>(state => state.idlesDetalization);

    const {
        isApiLoading: isEfficiencyLoading,
        data: efficiencyData,
        apiError: efficiencyDataError
    } = useTypedSelector<EfficiencySliceState>(state => state.efficiencyDetalization);

    const isEmpty = efficiencyData.totalTransportationDuration === 0
        && efficiencyData.totalWorkDuration === 0 && efficiencyData.totalIdleDuration === 0

    const error =
        (efficiencyDataError !== undefined ? efficiencyDataError + ". " : "")
        + (idlesApiError !== undefined ? idlesApiError : "")

    const isLoading = isEfficiencyLoading || isIdlesLoading

    return (
        <Sticker className={st['efficiency']}>
            {isLoading
                ? <LoadingBox borderRadius={'10'}/>
                : <EfficiencyContent setPeriod={setPeriod} period={period} isEmpty={Boolean(isEmpty)} error={error}/>
            }
        </Sticker>
    )
}

type EfficiencyContentProps = {
    period: WorkDisplayPeriod
    setPeriod: (period: WorkDisplayPeriod) => void
    isEmpty: boolean
    error: string
}

const EfficiencyContent: FC<EfficiencyContentProps> = ({ period, isEmpty, error, setPeriod }) => {
    const renderContent = () => {
        if (error) return <StickerErrorBox className={st['efficiency__error']} exception={error}/>
        if (isEmpty) return <EmptyPage/>
        return (
            <>
                <EfficiencyChart period={period}/>
                <Idles period={period}/>
            </>
        )

    }

    return (
        <>
            <PeriodSwitcher period={period} setPeriod={setPeriod}/>
            {renderContent()}
        </>
    )
}

const efficiencySelector: ApiSelect<EfficiencySliceState> =
    createSelector((state: RootState) => state.efficiencyDetalization, identity.of<EfficiencySliceState>);

export default stickerLoadingWithCrop(
    efficiencySelector,
    actions =>
        (p, params) => {
            const fetchParams = omit(params, 'period')
            return [
                actions.fetchIdleDetails(getWorkDetailsRequestParams(p, fetchParams)),
                actions.fetchEfficiency(getWorkDetailsRequestParams(p, fetchParams))
            ]
        },
    { cropEnabled: false, preventStickerBox: true }
)(Efficiency);